import { create } from 'zustand';
import type { Act } from '@/models/act';
import { readActsForPatient } from '@/services/firestore/acts';
import { parseAmountValue } from '@/lib/amount';

type ActsState = {
  byPatient: Record<string, Act[]>;
  totalPatientsBilled: Record<string, number>;
  loading: Record<string, boolean>;

  refresh: (uid: string, patientId: string) => Promise<void>;
  applyAddedActs: (acts: Act[]) => void;
  applyModifiedAct: (next: Act, prevAmount: number) => void;
  applyDeletedAct: (act: Act) => void;
  // Context the backend needs for visit pricing — derived from in-memory list
  // to avoid an extra Firestore query on every visit-button tap. Pass
  // excludeActId when re-pricing an existing act (edit flow) so the act
  // being modified is not counted as a prior billing of itself.
  visitContext: (
    patientId: string,
    code: string,
    excludeActId?: string,
  ) => {
    timesBilled: string;
    firstBilled: string;
    hasStab: string;
    previousVisit: string;
  };
};

export const useActsStore = create<ActsState>((set, get) => ({
  byPatient: {},
  totalPatientsBilled: {},
  loading: {},

  refresh: async (uid, patientId) => {
    set((s) => ({ loading: { ...s.loading, [patientId]: true } }));
    const list = await readActsForPatient({ uid, patientId });
    const sum = list.reduce((acc, a) => acc + parseAmountValue(a.amount), 0);
    set((s) => ({
      byPatient: { ...s.byPatient, [patientId]: list },
      totalPatientsBilled: { ...s.totalPatientsBilled, [patientId]: sum },
      loading: { ...s.loading, [patientId]: false },
    }));
  },

  applyAddedActs: (acts) => {
    if (acts.length === 0) return;
    const patientId = acts[0].patientId;
    const delta = acts.reduce((acc, a) => acc + parseAmountValue(a.amount), 0);
    set((s) => ({
      byPatient: {
        ...s.byPatient,
        [patientId]: [...(s.byPatient[patientId] ?? []), ...acts],
      },
      totalPatientsBilled: {
        ...s.totalPatientsBilled,
        [patientId]: (s.totalPatientsBilled[patientId] ?? 0) + delta,
      },
    }));
  },

  applyModifiedAct: (next, prevAmount) => {
    const delta = parseAmountValue(next.amount) - prevAmount;
    set((s) => ({
      byPatient: {
        ...s.byPatient,
        [next.patientId]: (s.byPatient[next.patientId] ?? []).map((a) =>
          a.id === next.id ? next : a,
        ),
      },
      totalPatientsBilled: {
        ...s.totalPatientsBilled,
        [next.patientId]: (s.totalPatientsBilled[next.patientId] ?? 0) + delta,
      },
    }));
  },

  applyDeletedAct: (act) => {
    const delta = -parseAmountValue(act.amount);
    set((s) => ({
      byPatient: {
        ...s.byPatient,
        [act.patientId]: (s.byPatient[act.patientId] ?? []).filter(
          (a) => a.id !== act.id,
        ),
      },
      totalPatientsBilled: {
        ...s.totalPatientsBilled,
        [act.patientId]: (s.totalPatientsBilled[act.patientId] ?? 0) + delta,
      },
    }));
  },

  visitContext: (patientId, code, excludeActId) => {
    const all = get().byPatient[patientId] ?? [];
    const list = excludeActId
      ? all.filter((a) => a.id !== excludeActId)
      : all;
    const sortedByDate = [...list].sort((a, b) =>
      a.billedAt.localeCompare(b.billedAt),
    );
    const timesBilled = list.filter((a) => a.code === code).length;
    const firstBilled = sortedByDate[0]?.billedAt ?? '';
    // hasStab + previousVisit: dead-letter since billing commit 8889473
    // (2021-04-08) removed the FRCP stab+previous-visit elabo upcharge from
    // the /act pricer. Still computed + shipped to preserve the Flutter wire
    // contract in case the rule is ever reactivated. No physician has
    // reported a missing upcharge in 5 years.
    const hasStab = list.some((a) => a.code === 'stab') ? 'true' : 'false';
    const previousVisit = sortedByDate[sortedByDate.length - 1]?.code ?? '';
    return {
      timesBilled: String(timesBilled),
      firstBilled,
      hasStab,
      previousVisit,
    };
  },
}));
